import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBolt, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OrganizationalUnitQualificationTrainingProviderApprovalStatus,
  QualificationCategoryIdsMap,
  QualificationTrainingProvider,
  QualificationTrainingProviderClass,
  RoleAction,
  RolePermissionSubject,
  SimpleQualificationV2,
  TrainingQualification,
  TrainingQualificationRow,
} from '@wilson/interfaces';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTableModule } from 'ng-zorro-antd/table';
import { ClassesRendererComponent } from './renderers/classes.renderer';
import { DeleteRendererComponent } from './renderers/delete.renderer';
import { ProviderRendererComponent } from './renderers/provider.renderer';
import { QualificationsRendererComponent } from './renderers/qualifications.renderer';
import { TrainingQualificationsGateway } from '@wilson/qualification-v2/common';
import { RouteRendererComponent } from './renderers/route.renderer';
import { FlowDirection } from '../../../../types';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_PAGE_NUMBER = 1;

export type QualificationId = { id: string };

interface ValidationErrors {
  provider?: boolean;
  class?: boolean;
  qualifications?: boolean;
  route?: boolean;
}

interface RowState {
  selectedClass: QualificationTrainingProviderClass | null;
  selectedQualifications: string[];
  classQualifications: string[];
  providerClasses: QualificationTrainingProviderClass[];
  isEditable: boolean;
  routeParts: string[] | null;
}

interface ValidationState {
  valid: TrainingQualificationRow[];
  invalid: TrainingQualificationRow[];
}

interface FormValidationErrors {
  [key: string]: ValidationErrors;
}

const createEmptyTrainingQualificationRow = (): TrainingQualificationRow => ({
  provider: null,
  class: null,
  qualifications: [],
  routeParts: null,
});

const createEmptyRowState = (): RowState => ({
  selectedClass: null,
  selectedQualifications: [],
  classQualifications: [],
  providerClasses: [],
  isEditable: true,
  routeParts: null,
});

@Component({
  selector: 'wilson-training-qualifications-table',
  templateUrl: './training-qualifications-table.component.html',
  styleUrls: ['./training-qualifications-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslatePipe,
    NzTableModule,
    NzCollapseModule,
    NzButtonModule,
    FontAwesomeModule,
    NzTagModule,
    NzSelectModule,
    ProviderRendererComponent,
    ClassesRendererComponent,
    QualificationsRendererComponent,
    DeleteRendererComponent,
    RouteRendererComponent,
  ],
})
export class TrainingQualificationsTableComponent implements OnInit, OnChanges {
  private readonly trainingQualificationsGateway = inject(
    TrainingQualificationsGateway,
  );
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() trainingQualifications!: TrainingQualification[] | null;
  @Input() providers: QualificationTrainingProvider[] = [];
  @Input() trainingQualificationRows!: FormControl<
    TrainingQualificationRow[] | null
  >;
  @Input() qualificationsFormControl!: FormControl<string[] | null>;
  @Output() hasInvalidRows = new EventEmitter<boolean>();
  @Output() validationState = new EventEmitter<ValidationState>();
  @Output() refreshProviders = new EventEmitter<void>();

  readonly faPlus = faPlus;
  readonly faBolt = faBolt;
  readonly RolePermissionSubject = RolePermissionSubject;
  readonly RoleAction = RoleAction;
  readonly can = inject(AbilityServiceSignal).can;
  private showQualificationsRendererByRow: Record<number, boolean> = {};
  private rowStates: Record<number, RowState> = {};

  public flowDirection: FlowDirection = FlowDirection.LeftToRight;
  public classesMap: Record<string, QualificationTrainingProviderClass[]> = {};
  public qualificationsMap: Record<string, SimpleQualificationV2[]> = {};
  selectedProvider: QualificationTrainingProvider | null = null;
  showQualificationsRenderer = true;
  routes: string[] = [];
  selectedRoutes: string[] = [];
  selectedStartLocation = '';

  ngOnInit(): void {
    this.initializeFormSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['providers']) {
      this.handleProvidersChange(changes['providers'].currentValue);
    }
  }

  addTrainingQualification(): void {
    if (this.hasEmptyQualificationsOrRoute()) {
      return;
    }
    this.prepareForNewRow();
    const newRow = createEmptyTrainingQualificationRow();
    this.addRowToForm(newRow);
    const newIndex = this.getCurrentFormValue().length - 1;
    this.showQualificationsRendererByRow[newIndex] = true;
  }

  onProviderChange(
    provider: QualificationTrainingProvider,
    index: number,
  ): void {
    this.selectedProvider = provider;
    if (this.flowDirection === FlowDirection.RightToLeft) {
      this.handleRightToLeftProviderChange(provider, index);
    } else {
      this.handleLeftToRightProviderChange(provider, index);
    }
  }

  onClassChange(
    selectedClass: QualificationTrainingProviderClass,
    index: number,
  ): void {
    this.updateRowState(index, { selectedClass });
    this.updateClassQualifications(index, selectedClass);
    if (this.selectedProvider?.providerIdentifier === 'synclogic') {
      this.handleSynclogicClassChange(selectedClass, index);
    }

    const classQualifications =
      selectedClass?.qualifications?.map((q) => q.id) ?? [];
    if (classQualifications.length === 1) {
      this.updateRowState(index, {
        selectedQualifications: classQualifications,
      });
    }

    const updatedRow = this.buildUpdatedRow(index, selectedClass);
    this.updateFormRow(index, updatedRow);

    this.checkAndDisableRowIfComplete(index);
  }

  onStartLocationChange(startLocation: string): void {
    this.selectedStartLocation = startLocation;
  }

  onEndLocationChange(endLocation: string, index: number): void | null {
    if (!this.selectedRoutes || this.selectedRoutes.length === 0) {
      console.warn('No route is available.');
      return null;
    }

    const startIndex = this.selectedRoutes.indexOf(this.selectedStartLocation);
    const endIndex = this.selectedRoutes.indexOf(endLocation);
    const routeSegment = this.selectedRoutes.slice(startIndex, endIndex + 1);

    const currentValue = this.getCurrentFormValue();
    const updatedRow = { ...currentValue[index], routeParts: routeSegment };

    this.updateRowState(index, { routeParts: routeSegment });
    this.updateFormRow(index, updatedRow);
  }

  onQualificationsChange(qualificationIds: string[], index: number): void {
    this.detectFlowDirectionChange(index);

    if (this.flowDirection === FlowDirection.RightToLeft) {
      this.handleRightToLeftQualificationsChange(qualificationIds, index);
    } else {
      this.handleLeftToRightQualificationsChange(qualificationIds, index);
    }
  }

  onDelete(index: number): void {
    const currentValue = this.getCurrentFormValue();
    const newValue = currentValue.filter((_, i) => i !== index);
    this.updateFormValue(newValue);
    this.reindexRowStates(index);
    this.cdr.detectChanges();
  }

  isQualificationsDisabled(index: number): boolean {
    const row = this.getCurrentFormValue()?.[index];
    if (row?.provider?.id === null) {
      return false;
    }
    return Boolean(row?.provider) && !this.getRowState(index).selectedClass;
  }

  isRowEditable(index: number): boolean {
    const rowState = this.getRowState(index);

    if (!rowState.isEditable) {
      return false;
    }

    if (this.flowDirection === FlowDirection.RightToLeft) {
      const currentValue = this.getCurrentFormValue();
      const row = currentValue?.[index];

      const hasProvider = !!row?.provider;
      const hasClass = !!rowState.selectedClass;
      const hasQualifications = rowState.selectedQualifications?.length > 0;

      if (hasProvider && hasClass && hasQualifications) {
        this.updateRowState(index, { isEditable: false });
        return false;
      }
    }

    return true;
  }

  get selectedClasses(): Record<
    number,
    QualificationTrainingProviderClass | null
  > {
    return this.createIndexedRecord('selectedClass');
  }

  get selectedQualifications(): Record<number, string[]> {
    return this.createIndexedRecord('selectedQualifications');
  }

  get classQualifications(): Record<number, string[]> {
    return this.createIndexedRecord('classQualifications');
  }

  get providerClasses(): Record<number, QualificationTrainingProviderClass[]> {
    return this.createIndexedRecord('providerClasses');
  }

  isQualificationsRendererVisible(index: number): boolean {
    return this.showQualificationsRendererByRow[index] ?? true;
  }

  private initializeFormSubscription(): void {
    this.trainingQualificationRows.valueChanges.subscribe((value) => {
      if (value) {
        this.validateFormState(value);
      }
    });
  }

  private handleSynclogicClassChange(
    selectedClass: QualificationTrainingProviderClass,
    index: number,
  ): void {
    console.log('selectedClass', selectedClass);
    if (
      selectedClass.qualifications.length === 1 &&
      selectedClass.qualifications[0].qualificationCategory.id ===
        QualificationCategoryIdsMap.Route
    ) {
      this.showQualificationsRendererByRow[index] = false;
    } else {
      this.showQualificationsRendererByRow[index] = true;
    }

    const route = selectedClass.qualifications[0].nameEn;
    const routeQualifications = route.split(' - ');
    this.selectedRoutes = routeQualifications;
    this.routes = routeQualifications;

    const currentValue = this.getCurrentFormValue();
    const updatedRow = {
      ...currentValue[index],
      class: selectedClass,
      qualifications: [],
    };
    this.updateFormRow(index, updatedRow);

    this.checkAndDisableRowIfComplete(index);
  }

  private handleProvidersChange(
    providers: QualificationTrainingProvider[],
  ): void {
    this.providers = providers;
    this.cdr.detectChanges();
  }

  private getCurrentFormValue(): TrainingQualificationRow[] {
    return this.trainingQualificationRows.value || [];
  }

  private updateFormValue(value: TrainingQualificationRow[]): void {
    this.trainingQualificationRows.setValue(value);
  }

  private updateFormRow(index: number, row: TrainingQualificationRow): void {
    const currentValue = this.getCurrentFormValue();
    const newValue = [...currentValue];
    newValue[index] = row;
    this.updateFormValue(newValue);
  }

  private addRowToForm(newRow: TrainingQualificationRow): void {
    const currentValue = this.getCurrentFormValue();
    this.updateFormValue([...currentValue, newRow]);
  }

  private getRowState(index: number): RowState {
    if (!this.rowStates[index]) {
      this.rowStates[index] = createEmptyRowState();
    }
    return this.rowStates[index];
  }

  private updateRowState(index: number, updates: Partial<RowState>): void {
    this.rowStates[index] = { ...this.getRowState(index), ...updates };
  }

  private clearRowState(index: number): void {
    delete this.rowStates[index];
    delete this.showQualificationsRendererByRow[index];
  }

  private reindexRowStates(deletedIndex: number): void {
    const newRowStates: Record<number, RowState> = {};
    const newShowQualificationsRendererByRow: Record<number, boolean> = {};

    Object.keys(this.rowStates).forEach((oldIndexStr) => {
      const oldIndex = Number(oldIndexStr);
      if (oldIndex < deletedIndex) {
        newRowStates[oldIndex] = this.rowStates[oldIndex];
        newShowQualificationsRendererByRow[oldIndex] =
          this.showQualificationsRendererByRow[oldIndex];
      } else if (oldIndex > deletedIndex) {
        newRowStates[oldIndex - 1] = this.rowStates[oldIndex];
        newShowQualificationsRendererByRow[oldIndex - 1] =
          this.showQualificationsRendererByRow[oldIndex];
      }
    });
    this.rowStates = newRowStates;
    this.showQualificationsRendererByRow = newShowQualificationsRendererByRow;
    this.cdr.detectChanges();
  }

  private setFlowDirection(direction: FlowDirection): void {
    this.flowDirection = direction;
  }

  private detectFlowDirectionChange(index: number): void {
    const currentValue = this.getCurrentFormValue();
    const row = currentValue?.[index];

    if (row?.qualifications?.length === 0 && row?.provider === null) {
      this.setFlowDirection(FlowDirection.RightToLeft);
    }
  }

  private hasEmptyQualificationsOrRoute(): boolean {
    return Object.values(this.rowStates).some(
      (rowState) =>
        rowState.selectedQualifications.length === 0 &&
        rowState.routeParts?.length === 0,
    );
  }

  private prepareForNewRow(): void {
    this.setFlowDirection(FlowDirection.LeftToRight);
    this.refreshProviders.emit();
    this.makeAllRowsNonEditable();
  }

  private makeAllRowsNonEditable(): void {
    Object.keys(this.rowStates).forEach((index) => {
      this.updateRowState(Number(index), { isEditable: false });
    });
  }

  private ensureRowExists(index: number): TrainingQualificationRow {
    const currentValue = this.getCurrentFormValue();
    if (!currentValue[index]) {
      currentValue[index] = createEmptyTrainingQualificationRow();
    }
    return currentValue[index];
  }

  private updateClassQualifications(
    index: number,
    selectedClass: QualificationTrainingProviderClass | null,
  ): void {
    const qualificationIds =
      selectedClass?.qualifications?.map((q) => q.id) ?? [];
    this.updateRowState(index, { classQualifications: qualificationIds });
  }

  private buildUpdatedRow(
    index: number,
    selectedClass: QualificationTrainingProviderClass | null,
  ): TrainingQualificationRow {
    const baseRow =
      this.getCurrentFormValue()[index] ||
      createEmptyTrainingQualificationRow();

    if (this.flowDirection === FlowDirection.LeftToRight) {
      return {
        ...baseRow,
        class: selectedClass,
        qualifications: this.getRowState(index).classQualifications,
      };
    } else {
      return {
        ...baseRow,
        class: selectedClass,
        qualifications: this.getRowState(index).selectedQualifications,
      };
    }
  }

  private handleLeftToRightProviderChange(
    provider: QualificationTrainingProvider,
    index: number,
  ): void {
    this.updateRowState(index, {
      selectedClass: null,
      providerClasses: provider?.qualificationTrainingProviderClasses ?? [],
    });

    const baseRow = this.ensureRowExists(index);
    const updatedRow = { ...baseRow, provider };
    this.updateFormRow(index, updatedRow);
  }

  private handleRightToLeftProviderChange(
    provider: QualificationTrainingProvider,
    index: number,
  ): void {
    this.updateProviderClassesForQualifications(provider, index);

    const updatedRow = {
      provider,
      class: this.getRowState(index).selectedClass,
      qualifications: this.getRowState(index).selectedQualifications || [],
    };

    this.updateFormRow(index, updatedRow);

    this.checkAndDisableRowIfComplete(index);
  }

  private updateProviderClassesForQualifications(
    provider: QualificationTrainingProvider,
    index: number,
  ): void {
    const selectedQualifications =
      this.getRowState(index).selectedQualifications;

    const matchingClass = provider.qualificationTrainingProviderClasses.find(
      (qualificationTrainingProviderClass) =>
        qualificationTrainingProviderClass.qualifications.some((q) =>
          selectedQualifications.includes(q.id),
        ),
    );

    if (matchingClass) {
      this.updateRowState(index, { providerClasses: [matchingClass] });
    }
  }

  private handleLeftToRightQualificationsChange(
    qualificationIds: string[],
    index: number,
  ): void {
    this.updateRowState(index, {
      selectedQualifications: qualificationIds,
      classQualifications: qualificationIds,
    });

    const baseRow = this.ensureRowExists(index);
    const updatedRow = { ...baseRow, qualifications: qualificationIds };
    this.updateFormRow(index, updatedRow);
  }

  private handleRightToLeftQualificationsChange(
    qualificationIds: string[],
    index: number,
  ): void {
    this.updateRowState(index, {
      selectedQualifications: qualificationIds,
      classQualifications: qualificationIds,
    });

    const updatedRow = {
      provider: this.selectedProvider,
      class: null,
      qualifications: qualificationIds,
    };

    this.updateFormRow(index, updatedRow);

    if (qualificationIds.length > 0) {
      this.fetchProvidersForQualifications(qualificationIds);
    } else {
      this.resetRightToLeftFlow(index);
    }

    this.checkAndDisableRowIfComplete(index);
  }

  private fetchProvidersForQualifications(qualificationIds: string[]): void {
    this.trainingQualificationsGateway
      .getQualificationTrainingProviders({
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_PAGE_SIZE,
        qualificationIds,
        approvalStatuses: [
          OrganizationalUnitQualificationTrainingProviderApprovalStatus.Accepted,
        ],
      })
      .subscribe((providers) => {
        this.providers = providers.data;
        this.setFlowDirection(FlowDirection.RightToLeft);
        this.cdr.detectChanges();
      });
  }

  private resetRightToLeftFlow(index: number): void {
    this.setFlowDirection(FlowDirection.LeftToRight);

    const currentValue = this.getCurrentFormValue();
    const filteredValue = currentValue.filter((_, i) => i !== index);
    this.updateFormValue(filteredValue);

    this.clearRowState(index);
    this.refreshProviders.emit();
    this.cdr.detectChanges();
  }

  private validateFormState(rows: TrainingQualificationRow[]): void {
    const validationErrors: FormValidationErrors = {};
    let hasInvalid = false;
    const validRows: TrainingQualificationRow[] = [];
    const invalidRows: TrainingQualificationRow[] = [];

    rows.forEach((row, index) => {
      const rowErrors = this.validateRow(row, index);
      if (Object.keys(rowErrors).length > 0) {
        validationErrors[`row_${index}`] = rowErrors;
        hasInvalid = true;
        invalidRows.push(row);
      } else {
        validRows.push(row);
      }
    });

    this.setFormErrors(validationErrors);
    this.emitValidationState(hasInvalid, validRows, invalidRows);
  }

  private validateRow(
    row: TrainingQualificationRow,
    index: number,
  ): ValidationErrors {
    const errors: ValidationErrors = {};

    if (
      row.provider?.id === null &&
      (row.qualifications?.length ?? 0) > 0 &&
      row.routeParts === null
    ) {
      return errors;
    }

    if (!row.provider) {
      errors['provider'] = true;
    }

    const selectedClass = this.getRowState(index).selectedClass;
    if (!selectedClass) {
      errors['class'] = true;
    }

    const selectedCount =
      this.getRowState(index).selectedQualifications?.length ?? 0;
    const qualificationsCount = row.qualifications?.length ?? 0;

    if (selectedCount === 0 && row.routeParts?.length === 0) {
      errors['qualifications'] = true;
    }

    if (row.routeParts?.length === 0 && qualificationsCount === 0) {
      errors['route'] = true;
    }

    return errors;
  }

  private setFormErrors(validationErrors: FormValidationErrors): void {
    if (Object.keys(validationErrors).length > 0) {
      this.trainingQualificationRows.setErrors(validationErrors);
    } else {
      this.trainingQualificationRows.setErrors(null);
    }
  }

  private emitValidationState(
    hasInvalid: boolean,
    validRows: TrainingQualificationRow[],
    invalidRows: TrainingQualificationRow[],
  ): void {
    this.hasInvalidRows.emit(hasInvalid);
    this.validationState.emit({ valid: validRows, invalid: invalidRows });
  }

  private createIndexedRecord<K extends keyof RowState>(
    property: K,
  ): Record<number, RowState[K]> {
    const result: Record<number, RowState[K]> = {};
    Object.keys(this.rowStates).forEach((indexStr) => {
      const index = Number(indexStr);
      result[index] = this.rowStates[index][property];
    });
    return result;
  }

  private checkAndDisableRowIfComplete(index: number): void {
    if (this.flowDirection === FlowDirection.RightToLeft) {
      const currentValue = this.getCurrentFormValue();
      const row = currentValue?.[index];
      const rowState = this.getRowState(index);

      const hasProvider = !!row?.provider;
      const hasClass = !!rowState.selectedClass;
      const hasQualifications = rowState.selectedQualifications?.length > 0;

      if (hasProvider && hasClass && hasQualifications) {
        this.updateRowState(index, { isEditable: false });
        this.cdr.detectChanges();
      }
    }
  }

  public clearInternalState(): void {
    this.rowStates = {};
    this.classesMap = {};
    this.qualificationsMap = {};
    this.selectedProvider = null;
    this.flowDirection = FlowDirection.LeftToRight;
    this.cdr.detectChanges();
  }
}
